import * as Sentry from '@sentry/react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getRoutePath } from '@/routes/util';
import { useEffect, useRef } from 'react';
import { EmptyState, Flex } from '@kognitos/lattice';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

const ErrorBoundaryFallback = ({ resetError }: { resetError: () => void }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const originalPathname = useRef(location.pathname); // Track the path where error occurred

  // eslint-disable-next-line no-restricted-syntax -- reason: Reset error boundary when route changes
  useEffect(() => {
    // ONLY reset if the user has navigated away from the broken page
    if (location.pathname !== originalPathname.current) {
      resetError();
    }
  }, [location.pathname, resetError]);

  const { t } = useTranslation('common');
  return (
    <Flex justify="center" align="center" width="full" height="full">
      <EmptyState
        icon="AlertCircle"
        title={t('common.error.title')}
        description={t('common.error.subtitle')}
        actionLabel={t('common.action.goHome')}
        onAction={() => {
          navigate(getRoutePath('HOME'));
        }}
        secondaryActionLabel={t('common.action.reload')}
        onSecondaryAction={() => window.location.reload()}
        className="max-w-md"
      />
    </Flex>
  );
};

/**
 * Reusable Sentry ErrorBoundary wrapper with standard fallback
 */
const AppErrorBoundary: React.FC<ErrorBoundaryProps> = ({ children }) => {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <ErrorBoundaryFallback resetError={resetError} />
      )}
    >
      {children}
    </Sentry.ErrorBoundary>
  );
};

export default AppErrorBoundary;
